import { useEffect, useMemo, useRef, useState } from "react";
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  type SimulationLinkDatum,
  type SimulationNodeDatum,
} from "d3-force";
import { type NodoRedDocumento, type RedDocumento } from "../api.ts";
import { C, nodoColor, schemaEs } from "../theme.ts";
import { rutaAHash } from "../rutas.ts";

/**
 * GrafoDocumento — grafo force-directed de las afirmaciones extraídas de un
 * documento. Los nodos son sujetos/objetos por nombre normalizado (no
 * entidades canónicas); cuando una mención del documento resuelve a una
 * entidad canónica, el nodo lleva anillo oro y el clic abre su ficha.
 *
 * Interacción completa (misma familia que Red.tsx): rueda para acercar, pan
 * arrastrando el fondo, arrastrar nodos, clic → ficha. Más controles para
 * descongestionar la maraña: ocultar hojas (grado 1) y ver solo los nombres
 * con candidato canónico, ambos recalculan el layout.
 */

type SimNodo = NodoRedDocumento & SimulationNodeDatum & { grado: number };
type SimLink = SimulationLinkDatum<SimNodo> & { predicate: string; estado: string };

interface AristaPos {
  src: string;
  dst: string;
  predicate: string;
  estado: string;
}

const ANCHO = 1200;
const ALTO = 720;
const TICKS = 320;
const MAX_ETIQUETAS = 45;
const CLICK_TOLERANCIA_PX = 5;

interface Transform {
  k: number;
  tx: number;
  ty: number;
}

function radio(n: SimNodo): number {
  return 5 + Math.min(11, Math.sqrt(n.grado + n.n_afirmaciones) * 1.7);
}

function colorNodo(n: NodoRedDocumento): string {
  // Nodo resuelto: color por el tipo canónico de su entidad. Sin resolver:
  // neutro cálido, para que los que sí conectan al grafo resalten.
  if (n.entidad_schema) return nodoColor(n.entidad_schema);
  return "#9FB2AF";
}

function filtrar(red: RedDocumento, ocultarHojas: boolean, soloCandidato: boolean): RedDocumento {
  let nodos = red.nodos;
  if (soloCandidato) nodos = nodos.filter((n) => n.entidad_id !== null);
  let norms = new Set(nodos.map((n) => n.norm));
  let aristas = red.aristas.filter((a) => norms.has(a.src) && norms.has(a.dst));

  if (ocultarHojas) {
    // Un nodo es "hoja" si toca una sola arista; ocultarlos deja el esqueleto
    // de los nombres realmente conectados. Se aplica UNA vez (no en cascada)
    // para no vaciar cadenas largas legítimas.
    const grado = new Map<string, number>();
    for (const a of aristas) {
      grado.set(a.src, (grado.get(a.src) ?? 0) + 1);
      grado.set(a.dst, (grado.get(a.dst) ?? 0) + 1);
    }
    nodos = nodos.filter((n) => (grado.get(n.norm) ?? 0) >= 2 || n.entidad_id !== null);
    norms = new Set(nodos.map((n) => n.norm));
    aristas = aristas.filter((a) => norms.has(a.src) && norms.has(a.dst));
  }
  return { ...red, nodos, aristas };
}

function layout(red: RedDocumento): { nodos: SimNodo[]; aristas: AristaPos[] } {
  const grado = new Map<string, number>();
  for (const a of red.aristas) {
    grado.set(a.src, (grado.get(a.src) ?? 0) + 1);
    grado.set(a.dst, (grado.get(a.dst) ?? 0) + 1);
  }
  const nodos: SimNodo[] = red.nodos.map((n) => ({ ...n, grado: grado.get(n.norm) ?? 0 }));
  const links: SimLink[] = red.aristas
    .filter((a) => grado.has(a.src) && grado.has(a.dst))
    .map((a) => ({ source: a.src, target: a.dst, predicate: a.predicate, estado: a.estado }));

  const sim = forceSimulation(nodos)
    .force("link", forceLink<SimNodo, SimLink>(links).id((d) => d.norm).distance(110).strength(0.35))
    .force("charge", forceManyBody().strength(-420))
    .force("center", forceCenter(ANCHO / 2, ALTO / 2))
    .force("collide", forceCollide<SimNodo>().radius((d) => radio(d) + 14))
    .stop();
  for (let i = 0; i < TICKS; i++) sim.tick();

  const xs = nodos.map((n) => n.x ?? 0);
  const ys = nodos.map((n) => n.y ?? 0);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const margen = 70;
  const escala = Math.min(
    (ANCHO - 2 * margen) / Math.max(1, maxX - minX),
    (ALTO - 2 * margen) / Math.max(1, maxY - minY),
  );
  const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
  for (const n of nodos) {
    n.x = ANCHO / 2 + ((n.x ?? 0) - cx) * escala;
    n.y = ALTO / 2 + ((n.y ?? 0) - cy) * escala;
  }
  return {
    nodos,
    aristas: links.map((l) => ({
      src: (l.source as SimNodo).norm,
      dst: (l.target as SimNodo).norm,
      predicate: l.predicate,
      estado: l.estado,
    })),
  };
}

export function GrafoDocumento({ red }: { red: RedDocumento }) {
  const [ocultarHojas, setOcultarHojas] = useState(false);
  const [soloCandidato, setSoloCandidato] = useState(false);
  const [nodos, setNodos] = useState<SimNodo[]>([]);
  const [aristas, setAristas] = useState<AristaPos[]>([]);
  const [tf, setTf] = useState<Transform>({ k: 1, tx: 0, ty: 0 });
  const [hover, setHover] = useState<{ nodo: SimNodo; x: number; y: number } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const drag = useRef<
    | { tipo: "nodo"; norm: string; movido: number }
    | { tipo: "pan"; x0: number; y0: number; tx0: number; ty0: number }
    | null
  >(null);

  const filtrada = useMemo(
    () => filtrar(red, ocultarHojas, soloCandidato),
    [red, ocultarHojas, soloCandidato],
  );

  useEffect(() => {
    const { nodos: ns, aristas: as } = layout(filtrada);
    setNodos(ns);
    setAristas(as);
    setTf({ k: 1, tx: 0, ty: 0 });
  }, [filtrada]);

  const porNorm = useMemo(() => new Map(nodos.map((n) => [n.norm, n])), [nodos]);
  const etiquetarTodos = nodos.length <= MAX_ETIQUETAS;
  const nResueltos = useMemo(() => nodos.filter((n) => n.entidad_id !== null).length, [nodos]);

  function aLienzo(clientX: number, clientY: number): { x: number; y: number } {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const sx = ((clientX - rect.left) / rect.width) * ANCHO;
    const sy = ((clientY - rect.top) / rect.height) * ALTO;
    return { x: (sx - tf.tx) / tf.k, y: (sy - tf.ty) / tf.k };
  }

  function onWheel(e: React.WheelEvent<SVGSVGElement>) {
    const factor = Math.exp(-e.deltaY * 0.0015);
    setTf((t) => {
      const k = Math.min(6, Math.max(0.25, t.k * factor));
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return { ...t, k };
      const sx = ((e.clientX - rect.left) / rect.width) * ANCHO;
      const sy = ((e.clientY - rect.top) / rect.height) * ALTO;
      return { k, tx: sx - ((sx - t.tx) / t.k) * k, ty: sy - ((sy - t.ty) / t.k) * k };
    });
  }

  function onPointerDownFondo(e: React.PointerEvent<SVGSVGElement>) {
    if (drag.current) return;
    drag.current = { tipo: "pan", x0: e.clientX, y0: e.clientY, tx0: tf.tx, ty0: tf.ty };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent<SVGSVGElement>) {
    const d = drag.current;
    if (!d) return;
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    if (d.tipo === "pan") {
      const dx = ((e.clientX - d.x0) / rect.width) * ANCHO;
      const dy = ((e.clientY - d.y0) / rect.height) * ALTO;
      setTf((t) => ({ ...t, tx: d.tx0 + dx, ty: d.ty0 + dy }));
    } else {
      d.movido += Math.abs(e.movementX) + Math.abs(e.movementY);
      const p = aLienzo(e.clientX, e.clientY);
      setNodos((ns) => ns.map((n) => (n.norm === d.norm ? { ...n, x: p.x, y: p.y } : n)));
      setHover(null);
    }
  }

  function onPointerUp() {
    drag.current = null;
  }

  function onPointerDownNodo(e: React.PointerEvent<SVGGElement>, n: SimNodo) {
    e.stopPropagation();
    drag.current = { tipo: "nodo", norm: n.norm, movido: 0 };
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
  }

  function onPointerUpNodo(e: React.PointerEvent<SVGGElement>, n: SimNodo) {
    const d = drag.current;
    drag.current = null;
    if (d && d.tipo === "nodo" && d.norm === n.norm && d.movido <= CLICK_TOLERANCIA_PX && n.entidad_id) {
      e.stopPropagation();
      window.location.hash = rutaAHash({ vista: "entidad", id: n.entidad_id });
    }
  }

  if (nodos.length === 0) {
    return (
      <div style={{ fontSize: 12.5, color: C.muted, padding: "10px 2px" }}>
        Ningún nombre cumple los filtros actuales.{" "}
        <button
          type="button"
          style={{ ...st, color: C.tiel, fontWeight: 600 }}
          onClick={() => { setOcultarHojas(false); setSoloCandidato(false); }}
        >
          Restablecer filtros
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: C.ink, cursor: "pointer" }}>
          <input type="checkbox" checked={ocultarHojas} onChange={(e) => { setOcultarHojas(e.target.checked); }} />
          Ocultar nombres sueltos
        </label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: C.ink, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={soloCandidato}
            disabled={nResueltos === 0}
            onChange={(e) => { setSoloCandidato(e.target.checked); }}
          />
          Solo con candidato en Nexo{nResueltos === 0 ? " (ninguno)" : ""}
        </label>
        <button type="button" style={btn} onClick={() => { setTf({ k: 1, tx: 0, ty: 0 }); }}>
          Reencuadrar
        </button>
        <span style={{ fontSize: 12, color: C.muted, marginLeft: "auto" }}>
          {String(nodos.length)} nombres · {String(aristas.length)} afirmaciones
        </span>
      </div>

      <div style={{ position: "relative", background: "#fff", border: `1px solid ${C.line}`, borderRadius: 12, overflow: "hidden" }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${String(ANCHO)} ${String(ALTO)}`}
          style={{ display: "block", width: "100%", height: "min(66vh, 720px)", cursor: "grab", touchAction: "none" }}
          onWheel={onWheel}
          onPointerDown={onPointerDownFondo}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={() => { setHover(null); drag.current = null; }}
          role="img"
          aria-label={`Red de afirmaciones extraídas del documento: ${String(nodos.length)} nombres y ${String(aristas.length)} afirmaciones. El detalle en tabla está debajo del grafo.`}
        >
          <g transform={`translate(${String(tf.tx)},${String(tf.ty)}) scale(${String(tf.k)})`}>
            {aristas.map((a, i) => {
              const s = porNorm.get(a.src);
              const d = porNorm.get(a.dst);
              if (!s || !d) return null;
              return (
                <line
                  key={i}
                  x1={s.x ?? 0}
                  y1={s.y ?? 0}
                  x2={d.x ?? 0}
                  y2={d.y ?? 0}
                  stroke={a.estado === "approved" ? C.tiel : "#C9BFA9"}
                  strokeWidth={(a.estado === "approved" ? 1.8 : 1.1) / tf.k}
                  strokeDasharray={a.estado === "approved" ? undefined : `${String(4 / tf.k)} ${String(3 / tf.k)}`}
                  opacity={0.75}
                />
              );
            })}
            {nodos.map((n) => {
              const r = radio(n);
              const resuelto = n.entidad_id !== null;
              const etiquetar = etiquetarTodos || resuelto || n.grado >= 4;
              return (
                <g
                  key={n.norm}
                  transform={`translate(${String(n.x ?? 0)},${String(n.y ?? 0)})`}
                  style={{ cursor: resuelto ? "pointer" : "grab" }}
                  onPointerDown={(e) => { onPointerDownNodo(e, n); }}
                  onPointerUp={(e) => { onPointerUpNodo(e, n); }}
                  onPointerEnter={(e) => { if (!drag.current) setHover({ nodo: n, x: e.clientX, y: e.clientY }); }}
                  onPointerLeave={() => { setHover((h) => (h?.nodo.norm === n.norm ? null : h)); }}
                >
                  {resuelto ? <circle r={r + 4} fill="none" stroke={C.oro} strokeWidth={2.4} /> : null}
                  <circle r={r} fill={colorNodo(n)} stroke="#fff" strokeWidth={1.6} />
                  {etiquetar ? (
                    <text
                      y={r + 11}
                      textAnchor="middle"
                      style={{
                        fontSize: Math.max(9, 10.5 / Math.sqrt(tf.k)),
                        fill: C.ink,
                        paintOrder: "stroke",
                        stroke: "#fff",
                        strokeWidth: 3,
                        strokeLinejoin: "round",
                        pointerEvents: "none",
                        fontWeight: resuelto ? 700 : 400,
                      }}
                    >
                      {n.texto.length > 32 ? `${n.texto.slice(0, 30)}…` : n.texto}
                    </text>
                  ) : null}
                </g>
              );
            })}
          </g>
        </svg>

        {hover ? (
          <div
            style={{
              position: "fixed",
              left: hover.x + 14,
              top: hover.y + 10,
              background: C.tielDark,
              color: "#fff",
              borderRadius: 8,
              padding: "8px 11px",
              fontSize: 12.5,
              maxWidth: 320,
              pointerEvents: "none",
              zIndex: 10,
              boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 2 }}>{hover.nodo.texto}</div>
            <div style={{ opacity: 0.85 }}>
              {String(hover.nodo.n_afirmaciones)} afirmación{hover.nodo.n_afirmaciones === 1 ? "" : "es"} en este documento
            </div>
            {hover.nodo.entidad_caption ? (
              <div style={{ color: "#F2DBA6", fontWeight: 700, marginTop: 2 }}>
                {schemaEs(hover.nodo.entidad_schema ?? "Unknown")}: {hover.nodo.entidad_caption}
                {hover.nodo.resolucion_estado === "approved" ? " (aprobado)" : " (propuesto)"}
                {hover.nodo.resolucion_score !== null ? ` · ${String(hover.nodo.resolucion_score)}` : ""}
              </div>
            ) : null}
            <div style={{ opacity: 0.7, marginTop: 3, fontSize: 11.5 }}>
              {hover.nodo.entidad_id ? "clic: abrir ficha · arrastrar: mover" : "arrastrar: mover"}
            </div>
          </div>
        ) : null}

        <div
          style={{
            position: "absolute",
            left: 10,
            bottom: 10,
            background: "rgba(255,255,255,0.92)",
            border: `1px solid ${C.line}`,
            borderRadius: 10,
            padding: "7px 11px",
            display: "flex",
            gap: 14,
            flexWrap: "wrap",
            fontSize: 12,
            color: C.ink,
            alignItems: "center",
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 11, height: 11, borderRadius: "50%", background: nodoColor("Person"), border: `2px solid ${C.oro}` }} />
            con candidato en Nexo
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 11, height: 11, borderRadius: "50%", background: "#9FB2AF" }} />
            solo en el documento
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 16, borderTop: "2px dashed #C9BFA9" }} />
            propuesta
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 16, borderTop: `2px solid ${C.tiel}` }} />
            aprobada
          </span>
        </div>
      </div>

      <p style={{ fontSize: 11.5, color: C.muted, margin: "8px 2px" }}>
        Rueda: acercar · arrastrar fondo: mover el lienzo · arrastrar nodo: acomodar · clic en nodo con
        candidato: abrir su ficha. Usa las casillas de arriba para descongestionar. El detalle completo,
        legible y ordenado está en las tablas de abajo.
      </p>
    </div>
  );
}

const btn: React.CSSProperties = {
  background: "#fff",
  border: `1px solid ${C.line}`,
  borderRadius: 8,
  padding: "5px 11px",
  fontSize: 12.5,
  fontWeight: 600,
  color: C.ink,
  cursor: "pointer",
};

const st: React.CSSProperties = {
  background: "transparent",
  border: "none",
  padding: 0,
  cursor: "pointer",
  font: "inherit",
};
